import React, { useState } from 'react';
import { MonitoredAudioTrackingTypeLabels, MonitoredAudioTrackingTypes, MonitoredAudioUrl } from '@round/api';
import { Popover } from '@round/ui-kit';
import cardStyles from './MonitoringCard.module.css';
import styles from './AudioTrackingType.module.css';
import cn from 'classnames';
import Card from '../../../../../../../ui/Card/Card';
import { ReactComponent as SelectDropdownIcon } from '../../../../../../../SharedComponents/svg/SelectDropdownIndicator.svg';
import { ReactComponent as QuestionMarkIcon } from '../../../../../../../assets/QuestionMark.svg';
import LoadingSpinner from '../../../../../../../SharedComponents/LoadingSpinner/LoadingSpinner';
import { getDeadlineFromNow, getEnhancedUntilDate } from './AudioTrackingType.helpers';

// Windows live in the UI and translate to a deadline datetime; the backend only stores the deadline.
const ENHANCEMENT_WINDOWS: { label: string; days: number }[] = [
    { label: '2 weeks', days: 14 },
    { label: '3 months', days: 90 },
];

type TrackingTypeOption = {
    label: string;
    trackingType: MonitoredAudioTrackingTypes;
    windowDays?: number;
};

// The single "Enhanced" entry is replaced by one option per window; each sets the matching deadline.
const options: TrackingTypeOption[] = [
    {
        label: MonitoredAudioTrackingTypeLabels.get(MonitoredAudioTrackingTypes.Basic) ?? '',
        trackingType: MonitoredAudioTrackingTypes.Basic,
    },
    ...ENHANCEMENT_WINDOWS.map((enhancementWindow) => ({
        label: `Enhance for ${enhancementWindow.label}`,
        trackingType: MonitoredAudioTrackingTypes.Enhanced,
        windowDays: enhancementWindow.days,
    })),
    {
        label: MonitoredAudioTrackingTypeLabels.get(MonitoredAudioTrackingTypes.Engagement) ?? '',
        trackingType: MonitoredAudioTrackingTypes.Engagement,
    },
];

type Props = {
    monitoredAudio: MonitoredAudioUrl;
    loading: boolean;
    onChange: (type: MonitoredAudioTrackingTypes, trackingTypeOverrideUntil?: string) => void;
};

const AudioTrackingType = ({ monitoredAudio, onChange, loading }: Props) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [deadlineAnchor, setDeadlineAnchor] = useState<HTMLElement | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const isEnhanced = monitoredAudio.tracking_type === MonitoredAudioTrackingTypes.Enhanced;
    const enhancedUntil = getEnhancedUntilDate(monitoredAudio.tracking_type_override_until);

    return (
        <>
            <div ref={setAnchor} className={cn(cardStyles.card, styles.card)}>
                <div className={styles.titleContainer}>
                    <p
                        className={cn(cardStyles.title, {
                            [styles.positive]: isEnhanced,
                        })}
                    >
                        {MonitoredAudioTrackingTypeLabels.get(monitoredAudio.tracking_type)}
                    </p>

                    <div className={styles.indicators}>
                        {isEnhanced && enhancedUntil && (
                            <div
                                ref={setDeadlineAnchor}
                                className={styles.iconContainer}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <QuestionMarkIcon className={styles.icon} />
                            </div>
                        )}

                        {!loading && (
                            <SelectDropdownIcon
                                className={cn(styles.dropdownIcon, {
                                    [styles.dropdownOpen]: isDropdownOpen,
                                })}
                            />
                        )}

                        {loading && (
                            <LoadingSpinner
                                containerClassName={styles.loadingSpinnerContainer}
                                spinnerClassName={styles.loadingSpinner}
                            />
                        )}
                    </div>
                </div>
                <p className={cardStyles.subtitle}>Audio Tracking Type</p>
            </div>

            {isEnhanced && enhancedUntil && (
                <Popover
                    anchorElement={deadlineAnchor}
                    showOn="hover"
                    options={{
                        placement: 'bottom-start',
                        modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
                    }}
                >
                    <Card className={styles.tooltip}>
                        <p className={styles.tooltipText}>Enhanced until {enhancedUntil}</p>
                    </Card>
                </Popover>
            )}

            <Popover
                anchorElement={anchor}
                showOn="click"
                options={{
                    placement: 'bottom-start',
                    modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
                }}
                onShowPopoverChange={setIsDropdownOpen}
            >
                {(setShow) => (
                    <Card className={styles.dropdown}>
                        {options.map((option) => (
                            <div
                                key={option.label}
                                className={cn(styles.option, {
                                    [styles.selected]:
                                        option.windowDays === undefined &&
                                        option.trackingType === monitoredAudio.tracking_type,
                                })}
                                onClick={() => {
                                    setShow(false);
                                    if (option.windowDays !== undefined) {
                                        onChange(option.trackingType, getDeadlineFromNow(option.windowDays));
                                    } else {
                                        onChange(option.trackingType);
                                    }
                                }}
                            >
                                {option.label}
                            </div>
                        ))}
                    </Card>
                )}
            </Popover>
        </>
    );
};

export default AudioTrackingType;
